import { isPlatform } from '@ionic/react';
import {
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AttachmentUploader,
  AttachmentUploaderRef,
} from '../../components/attachment/attachment-uploader';
import {
  trackAttachmentUploadIntent,
  trackAttachmentUploaded,
} from '../../config/analytics';
import { ApplicationConfig, isLocalDev } from '../../config/config';
import { isImage } from '../../util/file-utils';
import { logger } from '../../util/logger';
import { useJwtProvider } from '../auth.hooks';
import {
  Attachment,
  AttachmentSource,
  useCreateAttachmentMutation,
} from '../graphql/generated';
import { useCurrentUser } from '../user.hooks';
import { useUploadFileLazy } from './file.hooks';

interface GetAttachmentUrlOptions {
  resolution?: ImageResolution;
}

type GetAttachmentUrlFn = (
  path: string,
  contentType: string,
  options?: GetAttachmentUrlOptions
) => string | undefined;

interface Dimension {
  width: number;
  height: number;
}

export enum ImageResolution {
  STANDARD,
  THUBMNAIL,
  HIGHRES,
}

const ImageResolutionLookup: Record<ImageResolution, Dimension> = {
  [ImageResolution.HIGHRES]: {
    width: 800,
    height: 800,
  },
  [ImageResolution.STANDARD]: {
    width: 400,
    height: 400,
  },
  [ImageResolution.THUBMNAIL]: {
    width: 100,
    height: 100,
  },
};

export interface OnFileUploadResult {
  attachment?: Attachment;
  fileKey?: string;
}

export interface AttachmentHelperOptions {
  sources: Pick<AttachmentSource, 'source' | 'sourceId'>[];
  accept?: string;
  onBeforeUpload?: () => void;
  onFileUpload?: (result: OnFileUploadResult) => void;
}

export interface AttachmentHelperResult {
  isUploading: boolean;
  error?: string;
  openFile: () => void;
  uploaderRef: RefObject<AttachmentUploaderRef>;
  uploader: ReactElement;
}

const useUploadAttachmentHelper = (
  options: AttachmentHelperOptions
): AttachmentHelperResult => {
  const { sources, onFileUpload, accept } = options;

  const uploadAttachment = useUploadAttachmentLazy();
  const uploaderRef = useRef<AttachmentUploaderRef>(null);

  const openFile = useCallback(() => {
    if (uploaderRef.current) {
      uploaderRef.current.openFile();
    }
  }, []);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);

  const onFileUploadInternal = useCallback(
    (result: OnFileUploadResult) => {
      if (result?.fileKey === undefined) {
        setUploadError('The attachment failed to upload');
      }
      setIsUploading(false);

      if (typeof onFileUpload === 'function') {
        onFileUpload(result);
      }
    },
    [onFileUpload]
  );

  // this is the function that will do the uploading
  const onFileChange = useCallback(
    async (file: File) => {
      await uploadAttachment(
        sources,
        file,
        () => {
          setIsUploading(true);
        },
        onFileUploadInternal
      );
    },
    [onFileUploadInternal, sources, uploadAttachment]
  );

  const result = useMemo((): AttachmentHelperResult => {
    return {
      isUploading,
      error: uploadError,
      uploaderRef,
      openFile,
      uploader: (
        <AttachmentUploader
          onFileChange={onFileChange}
          ref={uploaderRef}
          inputProps={{ accept }}
          clearFilesAfterChange={true}
        />
      ),
    };
  }, [isUploading, uploadError, uploaderRef, openFile, onFileChange, accept]);

  return result;
};

const useUploadAttachmentLazy = () => {
  const [uploadFile] = useUploadFileLazy();
  const [createAttachment] = useCreateAttachmentMutation();

  const uploadAttachment = useCallback(
    async (
      sources: Pick<AttachmentSource, 'source' | 'sourceId'>[],
      file: File,
      onBeforeFileUpload?: () => void,
      onUploadFile?: (result: OnFileUploadResult) => void,
      onError?: (error: unknown) => void
    ): Promise<Attachment | undefined> => {
      trackAttachmentUploadIntent(sources);

      try {
        const attachmentResult = await createAttachment({
          variables: {
            input: {
              contentType: file.type,
              name: file.name,
              size: file.size,
              sources,
            },
          },
        });

        const attachment = attachmentResult?.data?.createAttachment;

        const key = attachment?.path;
        if (key === undefined) {
          const { name, size, type } = file;
          logger.error('failed to create attachment', {
            attachment,
            sources,
            file: {
              name,
              size,
              type,
            },
          });
          return;
        }

        if (onBeforeFileUpload) {
          onBeforeFileUpload();
        }

        const fileKey = await uploadFile(file, undefined, key);
        trackAttachmentUploaded();

        if (fileKey === undefined) {
          logger.error('failed to upload attachment', {
            attachment,
          });
        }

        if (onUploadFile) {
          onUploadFile({
            attachment: attachment as Attachment,
            fileKey,
          });
        }

        return attachment as Attachment;
      } catch (error) {
        if (typeof onError === 'function') {
          onError(error);
        }
      }
    },
    [createAttachment, uploadFile]
  );

  return uploadAttachment;
};

const useAttachmentUrlLazy = (): GetAttachmentUrlFn => {
  const user = useCurrentUser();

  const [jwtToken, setJwtToken] = useState<string>();

  const jwtProvider = useJwtProvider({
    uri: ApplicationConfig.graphql.uri,
  });

  useEffect(() => {
    const getJwt = async () => {
      const jwt = await jwtProvider();
      setJwtToken(jwt?.token);
    };

    getJwt();
  }, [jwtProvider]);

  const callback = useCallback(
    (path: string, contentType: string, options?: GetAttachmentUrlOptions) => {
      if (!jwtToken || !user) {
        return;
      }

      let url = `${ApplicationConfig.files.domain}/${user.tenantId}/${path}`;

      const queryParams = new URLSearchParams();

      if (isLocalDev() || isPlatform('capacitor')) {
        queryParams.set('ba', jwtToken);
      }

      if (isImage(contentType)) {
        const dimension =
          ImageResolutionLookup[
            options?.resolution ?? ImageResolution.STANDARD
          ];

        if (dimension) {
          queryParams.set('width', `${dimension.width}`);
          queryParams.set('height', `${dimension.height}`);
        }
      }

      url = `${url}?${queryParams.toString()}`;

      return url;
    },
    [jwtToken, user]
  );

  return callback;
};

const useAttachmentUrl = (
  path: string,
  contentType: string,
  options?: GetAttachmentUrlOptions
): string | undefined => {
  const getAttachmentUrl = useAttachmentUrlLazy();
  return getAttachmentUrl(path, contentType, options);
};

export {
  useAttachmentUrl,
  useAttachmentUrlLazy,
  useUploadAttachmentHelper,
  useUploadAttachmentLazy,
};
